import { ComponentType, ReactNode, useMemo } from 'react';

import { format } from 'date-fns';

import { RatingItems, useModalController } from '@uikit';

import { DeserializedAppPrivacyCollected } from '@api';

import Basket from '@uikit/icons/Basket';
import DataCenter from '@uikit/icons/DataCenter';
import Envelope from '@uikit/icons/Envelope';
import HelpFilledCircle from '@uikit/icons/HelpFilledCircle';
import Identity from '@uikit/icons/Identity';
import Identity2 from '@uikit/icons/Identity2';
import LocationMarker from '@uikit/icons/LocationMarker';
import Lock3 from '@uikit/icons/Lock3';
import Mobile from '@uikit/icons/Mobile';
import Person from '@uikit/icons/Person';
import PhoneUsage from '@uikit/icons/PhoneUsage';
import Privacy from '@uikit/icons/Privacy';
import Wallet2 from '@uikit/icons/Wallet2';

import { useAuth } from '@shared/common/providers/AuthProvider';
import {
  useAuthModal,
  useCompleteMagicIdModal,
} from '@shared/common/providers/GlobalModalsProvider';
import { transformReview } from '@shared/helpers/review';

import I18nInfoTooltip from '@store:web/components/common/I18nInfoTooltip/I18nInfoTooltip';
import ReviewsSlider from '@store:web/components/common/ReviewsSlider/ReviewsSlider';
import { getTrustScoreLabel } from '@store:web/components/common/TrustScoreTag/TrustScoreTag';

import AddTrustReviewModal from './AddTrustReviewModal';
import { AppPageSectionProps } from './AppPage';
import ReviewListModal from './ReviewListModal';

const privacyDataIcons: Record<DeserializedAppPrivacyCollected, ComponentType> = {
  Purchases: Basket,
  'Device ID': Mobile,
  Location: LocationMarker,
  Name: Person,
  'Contact Info': HelpFilledCircle,
  'Wallet ID': Wallet2,
  'App Usage': PhoneUsage,
  Identifiers: Identity2,
  'Usage Data': DataCenter,
  Email: Envelope,
};

const AppTrustScoreSection = ({ app, token }: AppPageSectionProps) => {
  const {
    attributes: {
      allAudits,
      defaultTrustScore,
      verifiedDate,
      validatorsScore,
      privacyCollectedData,
    },
    trustReviews,
  } = app;
  const { isAuth, user } = useAuth();
  const reviews = useMemo(
    () => trustReviews?.map((review) => transformReview({ review })),
    [trustReviews],
  );
  const authModal = useAuthModal();
  const addTrustReviewModal = useModalController();
  const privacyCollection = privacyCollectedData?.filter((item) => item in privacyDataIcons);
  const trustScoreLabel = getTrustScoreLabel(defaultTrustScore);
  const completeMagicIdModal = useCompleteMagicIdModal();
  const scores: {
    Icon: ComponentType;
    label: ReactNode;
    hidden?: boolean;
    content:
      | {
          label?: string;
          content?: ReactNode;
        }[]
      | string;
    subContent?: ReactNode;
  }[] = [
    {
      Icon: Identity,
      label: (
        <>
          Audit{' '}
          <span className="whitespace-nowrap">
            Score{' '}
            <I18nInfoTooltip
              title="appPage_trustScore_auditScore_title"
              content="appPage_trustScore_auditScore_description"
            />
          </span>
        </>
      ),
      hidden: !token,
      content: [
        {
          label: 'No. Of. Audits',
          content: allAudits || 0,
        },
        {
          label: 'Score',
          content: (
            <span
              className={
                {
                  Low: 'text-secondary-700',
                  Medium: 'text-orange-700',
                  High: 'text-green-900',
                  'N/A': '',
                }[trustScoreLabel]
              }
            >
              {trustScoreLabel}
            </span>
          ),
        },
      ],
    },
    {
      Icon: Privacy,
      label: (
        <>
          Security Validators{' '}
          <span className="whitespace-nowrap">
            Score{' '}
            <I18nInfoTooltip
              title="appPage_trustScore_secValScore_title"
              content="appPage_trustScore_secValScore_description"
            />
          </span>
        </>
      ),
      content: [
        {
          label: 'Validation Date',
          content: verifiedDate ? format(new Date(verifiedDate * 1000), 'dd.MM.yyyy') : '-',
        },
        {
          content: (
            <RatingItems
              inactiveItemClassName="text-neutral-50"
              className="h-6 items-center rounded-full px-2 bg-secondary-200"
              score={Number(validatorsScore) || 0}
              itemClassName="icon-xs"
            />
          ),
        },
      ],
    },
    {
      Icon: Lock3,
      label: 'App Privacy',
      hidden: true,
      content: privacyCollection?.length
        ? 'The following data may be collected and linked to your identity:'
        : 'No data collected',
      subContent: (
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-4">
          {privacyCollection?.map((item) => {
            const Icon = privacyDataIcons[item];

            return (
              <li key={item} className="flex items-center t-body2 text-neutral-500 gap-4">
                <span className="icon p-1">
                  <Icon />
                </span>
                {item}
              </li>
            );
          })}
        </ul>
      ),
    },
  ];

  const handleAddTrustReviewButtonClick = () => {
    if (!isAuth) {
      authModal.open();

      return;
    }

    if (!user.attributes.name) {
      completeMagicIdModal.open({
        description: {
          name: null,
        },
        success: false,
      });

      return;
    }

    addTrustReviewModal.open();
  };

  return (
    <div className="flex gap-7 max-lg:flex-col">
      <section className="sm:app-page-section-card flex flex-col gap-8 flex-1">
        <header className="flex ">
          <h2 className="t-section-title" data-qa="app-trust-score-title">
            {app.attributes.appName} Magic Store Trust Score
          </h2>
        </header>

        <ul className="flex flex-col gap-10">
          {scores.map((score, i) => {
            const { Icon, hidden } = score;

            if (hidden) {
              return null;
            }

            return (
              <li key={i} className="flex flex-col gap-10">
                <div className="max-sm:flex-col flex flex-wrap gap-5 sm:items-center">
                  <span className="flex items-center t-subtitle1 gap-4 flex-1">
                    <span aria-hidden="true" className="icon text-primary-700">
                      <Icon />
                    </span>
                    <span className="">{score.label}</span>
                  </span>

                  {typeof score.content === 'string' ? (
                    <p className="flex-1 t-body4 text-neutral-500 text-center">{score.content}</p>
                  ) : (
                    <ul className="flex gap-3 flex-1">
                      {score.content.map((scoreContentItem, ci) => {
                        return (
                          <li
                            key={ci}
                            className="flex t-body2 items-center justify-center flex-1 basis-1/2 flex-col gap-2.5"
                            data-qa="trust-data"
                          >
                            {scoreContentItem.content && scoreContentItem.content}
                            {scoreContentItem.label && (
                              <span
                                className="t-body4 text-neutral-500 text-center"
                                data-qa="trust-data-title"
                              >
                                {scoreContentItem.label}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                {score.subContent}
              </li>
            );
          })}
        </ul>
      </section>
      <section
        className="sm:app-page-section-card flex flex-col gap-8 min-w-0 flex-1"
        data-qa="trust-review-block"
      >
        <header className="flex gap-5 justify-between">
          <h2 className="t-section-title" data-qa="app-trust-reviews-title">
            {app.attributes.appName} User Trust Reviews
          </h2>

          {!!reviews?.length && (
            <ReviewListModal
              title="User Trust Reviews"
              reviews={reviews}
              app={app}
              showReviewFlag
            />
          )}
        </header>

        {reviews?.length ? (
          <ReviewsSlider
            app={app.attributes}
            id="trust-reviews-slider"
            reviews={reviews.slice(0, 5)}
            className="-ml-store-container-px sm:-ml-app-page-section-card-p"
            sliderClassName="pl-store-container-px sm:pl-app-page-section-card-p"
            showReviewFlag
            showReviewFlagInModal
          />
        ) : (
          <p className="text-neutral-300" data-qa="trust-empty-reviews">
            Be the Pioneer of Trust - Post the First Review!
          </p>
        )}

        <button
          onClick={handleAddTrustReviewButtonClick}
          className="button button-solid button-lg flex-grow-0 self-start mt-auto"
        >
          + Add Trust Review
        </button>

        <AddTrustReviewModal app={app} {...addTrustReviewModal.props} />
      </section>
    </div>
  );
};

export default AppTrustScoreSection;
